import { ArrowRight } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent } from 'react';

import { Alert } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { Field } from '@/shared/ui/field';
import { Input } from '@/shared/ui/input';

import { fieldErrors, signInSchema, type SignInInput } from '../auth.schemas';

import { PasswordInput } from './password-input';

export interface SignInFormProps {
  onSubmit: (input: SignInInput) => Promise<string | null>;
}

/** Presentational sign-in form. `onSubmit` resolves to an error message or null on success. */
export function SignInForm({ onSubmit }: SignInFormProps) {
  const [values, setValues] = useState<SignInInput>({ email: '', password: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const form = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      form.current?.querySelector<HTMLInputElement>('[aria-invalid="true"]')?.focus();
    }
  }, [errors]);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (submitting) return;
    const parsed = signInSchema.safeParse(values);
    if (!parsed.success) {
      setErrors(fieldErrors(parsed.error));
      return;
    }
    setErrors({});
    setFormError(null);
    setSubmitting(true);
    try {
      setFormError(await onSubmit(parsed.data));
    } catch {
      setFormError('Could not connect. Check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form ref={form} onSubmit={handleSubmit} noValidate className="auth-form">
      {formError ? <Alert>{formError}</Alert> : null}
      <Field label="Email" htmlFor="email" error={errors['email']}>
        <Input
          id="email"
          type="email"
          autoComplete="email"
          placeholder="you@company.com"
          value={values.email}
          invalid={Boolean(errors['email'])}
          aria-describedby={errors['email'] ? 'email-description' : undefined}
          onChange={(e) => setValues((v) => ({ ...v, email: e.target.value }))}
        />
      </Field>
      <Field label="Password" htmlFor="password" error={errors['password']}>
        <PasswordInput
          id="password"
          autoComplete="current-password"
          placeholder="Enter your password"
          value={values.password}
          invalid={Boolean(errors['password'])}
          aria-describedby={errors['password'] ? 'password-description' : undefined}
          onChange={(e) => setValues((v) => ({ ...v, password: e.target.value }))}
        />
      </Field>
      <Button type="submit" className="mt-1 w-full" loading={submitting} loadingLabel="Signing in…">
        Continue to your residency <ArrowRight className="size-4" aria-hidden="true" />
      </Button>
    </form>
  );
}
